import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useDebounce } from "@ui/utils/useDebounce";
import { useAppDispatch, useAppSelector } from "@application/store/useStore";
import { getUserProfile } from "@reducers/users/thunk/user-thunk";
import { getLatestApActivityIso } from "@utility/access-point-monitoring";
// Transform API AccessPoint to UI format
const transformAccessPoint = (ap) => {
    return {
        id: ap.id || ap.accessPointUID,
        name: ap.accessPointName,
        deviceId: ap.accessPointUID,
        status: ap.isActive && ap.isConnected ? "active" : "inactive",
        lastUpdate: getLatestApActivityIso({
            endPointCommunicationMonitoring: ap.endPointCommunicationMonitoring,
            lastApCommunication: ap.lastApCommunication,
            updatedAt: undefined,
        }),
        devices: ap.nodeCount,
    };
};
export const useUserProfileData = (userId) => {
    const dispatch = useAppDispatch();
    const { userProfile, userProfileLoading, userProfileError } = useAppSelector((state) => state.user);
    const { impersonation } = useAppSelector((state) => state.auth);
    const [searchTerm, setSearchTerm] = useState("");
    const [filters, setFilters] = useState({
        status: "",
        name: "",
    });
    // Refs to prevent duplicate API calls
    const hasFetchedRef = useRef(false);
    const lastFetchedUserIdRef = useRef(null);
    const isFetchingRef = useRef(false);
    // Debounce search term
    const debouncedSearchTerm = useDebounce(searchTerm, 300);
    // Determine which userId to use - prioritize URL param, then impersonated user, then current user
    const effectiveUserId = userId ||
        (impersonation.isImpersonating ? impersonation.impersonatedUserId : null);
    // Fetch user profile when userId or impersonation state changes
    useEffect(() => {
        // Skip if no userId
        if (!effectiveUserId) {
            console.warn("No userId provided to useUserProfileData. userId:", userId, "impersonatedUserId:", impersonation.impersonatedUserId);
            return;
        }
        // Skip if already fetching the same userId
        if (isFetchingRef.current &&
            lastFetchedUserIdRef.current === effectiveUserId) {
            return;
        }
        // Skip if we already have the data for this userId (unless it's a refresh/page load)
        // On page refresh, userProfile might be null, so we need to fetch
        const shouldFetch = !hasFetchedRef.current ||
            lastFetchedUserIdRef.current !== effectiveUserId ||
            !userProfile ||
            userProfile.id !== effectiveUserId;
        if (shouldFetch) {
            isFetchingRef.current = true;
            lastFetchedUserIdRef.current = effectiveUserId;
            dispatch(getUserProfile(effectiveUserId))
                .unwrap()
                .then((profile) => {
                if (profile === null || profile === void 0 ? void 0 : profile.accessPoints) {
                }
                hasFetchedRef.current = true;
                isFetchingRef.current = false;
            })
                .catch((error) => {
                console.error("Error fetching user profile:", error);
                isFetchingRef.current = false;
                hasFetchedRef.current = false;
                lastFetchedUserIdRef.current = null;
            });
        }
        else {
        }
    }, [effectiveUserId, dispatch, impersonation.isImpersonating, userProfile]);
    // Filter access points based on search and filters
    const filteredAccessPoints = useMemo(() => {
        if (!(userProfile === null || userProfile === void 0 ? void 0 : userProfile.accessPoints))
            return [];
        let filtered = userProfile.accessPoints.map(transformAccessPoint);
        // Apply search filter
        if (debouncedSearchTerm) {
            filtered = filtered.filter((accessPoint) => accessPoint.name
                .toLowerCase()
                .includes(debouncedSearchTerm.toLowerCase()) ||
                accessPoint.deviceId
                    .toLowerCase()
                    .includes(debouncedSearchTerm.toLowerCase()));
        }
        // Apply status filter
        if (filters.status) {
            filtered = filtered.filter((accessPoint) => accessPoint.status === filters.status);
        }
        // Apply name filter
        if (filters.name) {
            filtered = filtered.filter((accessPoint) => accessPoint.name.toLowerCase().includes(filters.name.toLowerCase()));
        }
        return filtered;
    }, [userProfile === null || userProfile === void 0 ? void 0 : userProfile.accessPoints, debouncedSearchTerm, filters]);
    const handleFilterChange = useCallback((newFilters) => {
        setFilters((prev) => {
            const updated = Object.assign({}, prev);
            Object.entries(newFilters).forEach(([key, value]) => {
                if (value !== undefined) {
                    updated[key] = value;
                }
            });
            return updated;
        });
    }, []);
    const handleSearchChange = useCallback((event) => {
        setSearchTerm(event.target.value);
    }, []);
    return {
        user: userProfile,
        accessPoints: filteredAccessPoints,
        loading: userProfileLoading,
        error: userProfileError,
        searchTerm,
        filters,
        handleFilterChange,
        handleSearchChange,
    };
};
